// Статус оплаты брони для компактных мест — чип на доске (/bookings/board)
// и кнопка в списке (/bookings/list). Числовое ядро вынесено из
// бывшего calculatePaymentsMixin, чтобы подпись, цвет и legacy-строка считались из
// одного баланса, а не тремя копиями одной арифметики.
//
// Правило «оплачено» — parseInt(paid) == parseInt(total), как во всех
// calculatePayments/selectColorPaid проекта: копеечные хвосты не делают
// клиента должником. Здесь оно сохранено намеренно, ради паритета цветов
// с карточкой брони, календарями и клиентской страницей.
import { depositFieldFor } from "@/components/dashboard/bookings/addonsMath";

const SUM_FIELDS = [
  "rental_cost",
  "delivery",
  "delivery_end",
  "equipment",
  // FT-102 REQ-07: каталожное оборудование — компонент суммы «к оплате»
  "addons_cost",
  "insurance",
  "clean_payment",
  "add_drivers_cost",
  "hours_cost",
  "mileage_cost",
  "damage",
  "gas",
  "fine",
  "other",
  "other_end",
  "hours_cost_end",
];

// Нечисловое слагаемое (строка из v-model, null) считается нулём — так было
// в исходном mixin'е, иначе одна строка «1000» заразила бы итог NaN.
function numberOrZero(value) {
  return Number(value) === value ? value : 0;
}

function round2(value) {
  return parseFloat(value.toFixed(2));
}

// Сколько заплачено по завершённым платежам. При мультивалютных кассах
// платёж учитывается по rated_sum (сумма в базовой валюте), если он есть.
function paidTotal(counts, multicurrency) {
  let paid = 0;
  (counts || []).forEach((count) => {
    if (!count.completed) return;
    const useRated = multicurrency && count.rated_sum != null;
    const sum = Number(useRated ? count.rated_sum : count.sum);
    paid += count.operation ? sum : -sum;
  });
  return round2(paid);
}

/**
 * Баланс брони: { paid, total, diff, settled } или null для undefined-брони.
 * total включает залог у непринятых броней (не ride); diff = paid - total.
 */
export function bookingPaymentBalance(booking, { multicurrency = false } = {}) {
  if (booking == undefined) return null;
  let total = round2(SUM_FIELDS.reduce((acc, field) => acc + numberOrZero(booking[field]), 0));
  const paid = paidTotal(booking.counts, multicurrency);
  if (!booking.ride) {
    total += booking[depositFieldFor(booking)] || 0;
  }
  const diff = paid - total;
  return { paid, total, diff, settled: parseInt(paid) == parseInt(total) };
}

/**
 * Показывать ли расчётный чип/кнопку: есть хоть один платёж, либо аренда
 * идёт/завершена и бронь не техническая. Один предикат на доску и список —
 * в шаблоне списка он стоит в v-if, дублировать условие инлайном нельзя.
 */
export function hasPaymentChip(booking) {
  const counts = (booking && booking.counts) || [];
  return counts.length > 0 || Boolean((booking.in_rent || booking.ride) && !booking.technical);
}

// Сумма в подписи: до двух знаков, без хвостовых нулей («417», «417.4», «0.3»).
// Не Math.round: правило settled отстаёт от округления на целой границе
// (paid 99.7 / total 100), и целочисленная подпись давала «К оплате 0» красным.
function chipAmount(diff) {
  return Number(Math.abs(diff).toFixed(2));
}

// Красный только у настоящего должника: аренда идёт или завершена и бронь не
// техническая. Неоплаченная будущая/ожидающая бронь должником ещё не стала — amber.
function debtorColor(booking) {
  return (booking.in_rent || booking.ride) && !booking.technical ? "error" : "warning";
}

/**
 * Чип оплаты для доски и списка:
 *   { kind: "paid" | "to_pay" | "overpaid" | "not_paid", label, color, outlined }
 * или null, если чип показывать не надо (техническая бронь без платежей).
 * `t` — $t vue-i18n, `currency` — символ валюты компании (company.currency).
 */
export function bookingPaymentChip(booking, { t, currency = "", multicurrency = false } = {}) {
  const balance = bookingPaymentBalance(booking, { multicurrency });
  if (!balance) return null;
  if (!hasPaymentChip(booking)) {
    if (booking.technical) return null;
    return { kind: "not_paid", label: t("bookings.not_paid_2"), color: "primary", outlined: true };
  }
  if (balance.settled) {
    return { kind: "paid", label: t("bookings.paid"), color: "success", outlined: false };
  }
  const params = { sum: chipAmount(balance.diff), currency: currency || "" };
  if (balance.diff > 0) {
    return { kind: "overpaid", label: t("bookings.overpaid_chip", params).trim(), color: "warning", outlined: false };
  }
  return { kind: "to_pay", label: t("bookings.to_pay_chip", params).trim(), color: debtorColor(booking), outlined: false };
}

/**
 * Строка старого контракта calculatePayments: $t("bookings.paid") | "+ N" | " -N".
 * Её по-прежнему сравнивают с $t("bookings.paid") в :disabled и тултипах списка.
 */
export function legacyPaymentLabel(booking, { t, multicurrency = false } = {}) {
  const balance = bookingPaymentBalance(booking, { multicurrency });
  if (!balance) return undefined;
  if (balance.settled) return t("bookings.paid");
  return balance.diff >= 0 ? `+ ${Math.round(balance.diff)}` : ` ${Math.round(balance.diff)}`;
}
